
import {AppState, PermissionsAndroid, Platform, StyleSheet} from 'react-native';
import React, {useEffect, useRef} from 'react';
import messaging from '@react-native-firebase/messaging';
import {useNavigation} from '@react-navigation/native';
import notifee, {EventType} from '@notifee/react-native';
import { showToastNotification } from '../components/global/Toast';
import { getFCmToken } from '../utils/contants';
import { setAppOpenAdPaused } from '../utils/useOpenAppAd';

type NotificationData = {
  notificationType: 'LESSON_CREATED' | 'VOULT_CREATED' | 'HOTBET_CREATED';
  lessonId?: string;
  // Add other fields as needed
};

const InAppNotifications = () => {
  const initialMessageIdRef = useRef<string | null | any>(null); // Track launch message to prevent duplicate


  // Request permissions on mount
//   useEffect(() => {
//     requestUserPermission();
//   }, []);

  // Handle foreground notification presses via Notifee
  useEffect(() => {
    const unsubscribeForeground = notifee.onForegroundEvent(
      ({type, detail}) => {
        if (type === EventType.PRESS && detail.notification?.data) {
          const data = detail.notification.data as NotificationData;
        }
      },
    );

    return unsubscribeForeground;
  }, []);

  // Core FCM + AppState handling
  useEffect(() => {
    let notificationHandled = false;

    // Foreground messages: Show toast (local notification)
    const unsubscribeOnMessage = messaging().onMessage(async remoteMessage => {
      console.log('📩 onMessage fired: messageId:', remoteMessage.messageId);

      if (notificationHandled && Platform.OS === 'ios') {
        notificationHandled = false; // Reset for iOS transition quirk
        return;
      }

      // Skip if this is the duplicate from killed-state launch
      if (initialMessageIdRef.current === remoteMessage.messageId) {
        console.log(
          '⏭️ Skipping duplicate toast for launch messageId:',
          remoteMessage.messageId,
        );
        initialMessageIdRef.current = null; // Clear to allow future messages
        return;
      }

      const {title, body} = remoteMessage.notification ?? {};
      if (title && body) {
        showToastNotification(
          title,
          body,
          remoteMessage.data as NotificationData,
        );
      }
    });

    // Background: App opened from notification
    const unsubscribeOnOpened = messaging().onNotificationOpenedApp(
      remoteMessage => {
        console.log(
          '📩 Opened from background: messageId:',
          remoteMessage.messageId,
        );
      },
    );

    // Quit state: App launched from notification
    messaging()
      .getInitialNotification()
      .then(remoteMessage => {
        if (remoteMessage) {
          console.log(
            '🔥 Killed launch: Handling initial messageId:',
            remoteMessage.messageId,
          );
          initialMessageIdRef.current = remoteMessage.messageId; // Set for duplicate check
        }
      });

    // Background: Silent data-only (no UI, just log/process)
    messaging().setBackgroundMessageHandler(async remoteMessage => {
      console.log('📩 Background FCM (data-only):', remoteMessage);
      // Add silent processing if needed (e.g., update local state)
    });

    // Prevent duplicate toasts during app backgrounding (iOS quirk)
    const subscription = AppState.addEventListener('change', nextAppState => {
      if (nextAppState === 'background' || nextAppState === 'inactive') {
        notificationHandled = true;
      }
    });

    return () => {
      unsubscribeOnMessage();
      unsubscribeOnOpened();
      subscription.remove();
    };
  }, []);

  const fetchFcm = async (): Promise<void> => {
    try {
      const token = await getFCmToken();
      // TODO: Uncomment and integrate: userAction.updateToken(token);
      console.log('FCM Token fetched:', token);
    } catch (err) {
      console.error('@ Error fetching FCM token:', err);
    }
  };

  const requestUserPermission = async (): Promise<void> => {
    try {
      setAppOpenAdPaused(true);
      await requestNotificationPermission();
      const authorizationStatus = await messaging().requestPermission();
      if (
        authorizationStatus === messaging.AuthorizationStatus.AUTHORIZED ||
        authorizationStatus === messaging.AuthorizationStatus.PROVISIONAL
      ) {
        await fetchFcm();
        console.log('Notification permissions granted!');
      } else {
        console.log('Notification permissions denied!');
      }
    } finally {
      setAppOpenAdPaused(false, 1000);
    }
  };

  const requestNotificationPermission = async (): Promise<void> => {
    if (Platform.OS === 'android' && Platform.Version >= 33) {
      try {
        const granted = await PermissionsAndroid.request(
          'android.permission.POST_NOTIFICATIONS',
        );
        if (granted === PermissionsAndroid.RESULTS.GRANTED) {
          console.log('Android notification permission granted');
        } else {
          console.log('Android notification permission denied');
        }
      } catch (err) {
        console.warn('Android permission request error:', err);
      }
    } else {
      console.log('No additional permission needed for this platform');
    }
  };

  return null;
};

export default InAppNotifications;

const styles = StyleSheet.create({});
